import { memo } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";

import { IconType } from "~/common/components/icon/IconType";
import { Link } from "~/common/components/Link";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import RecaptchaWidget from "~/common/components/RecaptchaWidget/RecaptchaWidget";
import { CAPTCHA_ENABLED, EMAIL_PATTERN } from "~/utils/constants";
import { Controller } from "react-hook-form";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { Trans, useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import { DefaultButtonIcon, Icon } from "~/common/components/icon/Icon";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import { TERMS_OF_USE_PAGE } from "~/utils/url/staticUrls";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { MEDIA_QUERY_BELOW_SUPPORTED } from "~/common/utils/screenSizes";
import { Box, Button, Card, Stack, TextField } from "~/common/mui";
import { CustomerRegistrationBanner } from "~/features/registration/components/CustomerRegistrationBanner/CustomerRegistrationBanner";
import {
  ErrorMessage,
  HelperLabelErrorMessage,
  RegisterContactSalesSection,
  useRegisterForFree,
} from "~/features/registration/hooks/useRegisterForFree";

const RegisterForFreeMobile = ({ showCustomerRegistration }: { showCustomerRegistration?: boolean }) => {
  const { t } = useTranslation();
  const {
    errors,
    watch,
    control,
    resetPopupOpen,
    loginPopupOpen,
    openLogin,
    closeLogin,
    closeReset,
    onResetClick,
    onSubmitHandler,
  } = useRegisterForFree();

  const sizeIphoneFive = useMediaQuery(MEDIA_QUERY_BELOW_SUPPORTED);

  return (
    <div className="scha__register-free register-mobile">
      <Box sx={{ display: "flex", flexFlow: "column", gap: "32px" }}>
        {showCustomerRegistration && (
          <>
            <h1 className="scha__page-title">{t("page.registerFree.title")}</h1>
            <CustomerRegistrationBanner />
          </>
        )}
        <div className="scha__register-free__title">
          <PageTitle
            title={
              <Trans i18nKey={"page.registerFree.mobile.title"}>
                Register for free for
                <strong>medias</strong>
                <span className={"scha__register-free__medias-plus"}>Plus</span>
              </Trans>
            }
          />
          <h3 className="scha__register-free__title__subtitle">
            <Trans i18nKey={"page.registerFree.mobile.subtitle"}>
              With
              <strong>medias</strong>
              <span className={"scha__register-free__medias-plus"}>Plus</span>
              you get extended access to features and tools.
            </Trans>
          </h3>
        </div>
        <div className="scha__register-free form">
          <div className="scha__register-free__main-content">
            <form onSubmit={onSubmitHandler}>
              <Card
                component={Stack}
                gap={3}
                sx={{
                  borderRadius: 0,
                  padding: 2,
                }}
              >
                <Controller
                  name={"email"}
                  control={control}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={t("page.registerFree.label")}
                      inputRef={ref}
                      error={!!error}
                      helperText={<HelperLabelErrorMessage error={error} />}
                      inputProps={{ "data-test-id": "register-for-free-email" }}
                      {...inputProps}
                    />
                  )}
                  rules={{
                    required: { value: true, message: "error.invalidEmail" },
                    pattern: { value: EMAIL_PATTERN, message: "error.invalidEmail" },
                  }}
                />

                <Stack gap={2}>
                  <Controller
                    name={"generalTerms"}
                    control={control}
                    rules={{ required: { value: true, message: "error.tos" } }}
                    render={({ field: { value, onChange } }) => (
                      <CheckboxWithLabel
                        data-test-id={"register-for-free__general-terms"}
                        checked={value}
                        onCheck={onChange}
                      >
                        {t("page.registerFree.terms-part1")}{" "}
                        <a
                          className="scha__register-free__main-content__right__checkbox-area__text__link"
                          href={TERMS_OF_USE_PAGE}
                        >
                          {t("page.registerFree.terms-link")}
                        </a>{" "}
                        {t("page.registerFree.terms-part2")}
                      </CheckboxWithLabel>
                    )}
                  />
                  <ErrorMessage error={errors.generalTerms} />

                  <Controller
                    name={"privacyPolicy"}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MarketingConsentCheckbox
                        data-test-id={"register-for-free__privacy-policy"}
                        checked={value}
                        onCheck={onChange}
                      />
                    )}
                  />

                  <div className="scha__register-free__main-content__right__mandatory">
                    {t("page.registerFree.mandatory")}
                  </div>
                </Stack>
                {CAPTCHA_ENABLED && (
                  <Controller
                    name={"captcha"}
                    control={control}
                    rules={{ required: { value: true, message: "recaptcha.challenge.field.empty" } }}
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <RecaptchaWidget
                        onChange={onChange}
                        error={error?.message ? t(error.message) : ""}
                        size={sizeIphoneFive ? "compact" : "normal"}
                      />
                    )}
                  />
                )}

                <Button
                  // className={"mt-4"}
                  startIcon={<DefaultButtonIcon type={IconType.User} />}
                  fullWidth={true}
                  data-test-id={"register-for-free__submit"}
                  onClick={onSubmitHandler}
                >
                  {t("page.registerFree.register")}
                </Button>

                <div className="scha__register-free__already">
                  <span>{t("page.registerFree.already")} </span>
                  <Link onClick={openLogin} className="scha__register-free__already__link">
                    {t("page.registerFree.already-link")}
                  </Link>
                </div>

                <RegisterContactSalesSection />
              </Card>
            </form>
            {loginPopupOpen && <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />}
            {resetPopupOpen && <ResetPasswordPopup initialEmail={watch("email")} close={closeReset} />}
          </div>
        </div>
      </Box>

      <RegistrationMediasTable />
    </div>
  );
};

const RegistrationMediasTable = () => {
  const { t } = useTranslation();

  const options = [
    {
      label: t("page.registerForFree.baner.all"),
      medias: t("page.registerForFree.baner.limited"),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.lc"),
      medias: t("page.registerForFree.baner.limited"),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.cad"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.saveLinear"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.compareProducts"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.baner.trainingCampus"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.keep.new"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
    {
      label: t("page.registerForFree.banner.send.new"),
      medias: (
        <div className="circle disabled">
          <Icon type={IconType.Close} />
        </div>
      ),
      mediasPlus: (
        <div className="circle">
          <Icon type={IconType.Check} />
        </div>
      ),
    },
  ];

  return (
    <div className="options">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align={"center"}>
              <MediasLogo variant={"general"} />
            </TableCell>
            <TableCell align={"center"}>
              <MediasLogo variant={"plus"} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {options.map((option, i) => (
            <TableRow key={i}>
              <TableCell className={"label"}>{option.label}</TableCell>
              <TableCell className={"medias"}>{option.medias}</TableCell>
              <TableCell className={"mediasPlus"}>{option.mediasPlus}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default memo(RegisterForFreeMobile);
